/* eslint-disable */
import React, { useState, useEffect, useContext } from 'react';
import './travelCost.scss';
import MagnifyingGlass from '../../../Assets/images/svg/dar_magnifying.svg';
import DataGridComponent from '../../../Components/DataGrid/dataGrid';
import ButtonComponent from '../../../Components/Button/button';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '../../../Assets/images/svg/dar_pencil.svg';
import { TextField } from '@mui/material';
import { formatCurrency } from '../costMasterUtils';
import UseAxios from '../../../Api/http/http';
import { getCloudCost, getTravelDestination, getTravelCostDetails, saveTravelCost, updateTravelCost } from '../../../Api/request/costMaster';
import { AuthContext } from '../../../Contexts/AuthContext';
import { MODULE_NAMES } from '../../../Constants/modules';
import SearchDropdown from '../../../Components/Dropdown/dropdown';
import { v4 as uuidv4 } from 'uuid';
import { useSnackbar } from '../../../Components/Snackbar/snackBarProvider';

const TravelCost = () => {
  const [rows, setRows] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [isMobileView, setIsMobileView] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isLaptopView, setIsLaptopView] = useState(false);
  const [dropdownData, setDropdownData] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const { userProfile } = useContext(AuthContext);
  const { snackbarOpen } = useSnackbar();

  const permission = userProfile?.permissions
    ? userProfile.permissions[MODULE_NAMES.MASTER_COST_MASTER] || []
    : [];

  const levelOptions = [
    { "id": 1, "name": "Below Manager" },
    { "id": 2, "name": "Manager & Above" }
  ];

  const hasAccess = (action) => {
    return permission.includes(action);
  };

  useEffect(() => {
    if (isEdit) {
      setIsEdit(false);
    }
  }, [rows]);

  const checkMobileView = () => {
    setIsMobileView(window.innerWidth <= 768);
    setIsLaptopView(window.innerWidth <= 1024);
  };

  const getColumnWidth = (mobile, laptop, defaultWidth) => {
    if (isMobileView) {
      return mobile;
    } else if (isLaptopView) {
      return laptop;
    }
    return defaultWidth;
  };

  const getColumnFlex = (defaultFlex) => {
    if (!isMobileView && !isLaptopView) {
      return defaultFlex;
    }
    return undefined;
  };

  const handleSearch = (event) => {
    setSearchText(event.target.value.toLowerCase());
  };

  const fetchData = async () => {
    try {
      const [dropdownResponse, currencyResponse, travelCostResponse] =
        await Promise.all([
          UseAxios(getTravelDestination()),
          UseAxios(getCloudCost()),
          UseAxios(getTravelCostDetails()),
        ]);

      const dropdownData = dropdownResponse.data.map((city) => ({ ...city, name: city.city_name }));
      const currencies = currencyResponse.data.data.currencies;
      setDropdownData(dropdownData);
      setCurrencies(currencies);

      const transformedData = travelCostResponse.data.map((item, index) =>
        transformTravelCostData(item, index, dropdownData, currencies)
      );
      setRows(transformedData);
    } catch (error) {
      console.error('Error fetching data:', error.message || error);
      snackbarOpen('Oops! Please try again', 'warning');
    }
  };

  const transformTravelCostData = (item, index, dropdownData, currencies) => {
    const city = dropdownData.find((city) => city.id === item.city_id);
    const transportCurrency = currencies.find((currency) => currency.id === item.transport_cost_currency);
    const stayCurrency = currencies.find((currency) => currency.id === item.stay_cost_currency);
    const otherCurrency = currencies.find((currency) => currency.id === item.other_cost_currency);
    return {
      id: item.id,
      srNo: index + 1,
      City: city?.city_name || '',
      Country: city?.country_name || '',
      Level: item.level,
      travelCost: item.transport_cost,
      stayCostDay: item.stay_cost,
      otherCostDay: item.other_cost,
      remarks: item.remarks,
      transportCurrency: transportCurrency?.name,
      stayCurrency: stayCurrency?.name,
      otherCurrency: otherCurrency?.name,
      isEditing: false,
    };
  };

  useEffect(() => {
    fetchData();
    checkMobileView();
    window.addEventListener('resize', checkMobileView);
    return () => {
      window.removeEventListener('resize', checkMobileView);
    };
  }, []);

  const handleAddRow = () => {
    const newRow = {
      id: uuidv4(),
      srNo: rows.length + 1,
      City: '',
      Country: '',
      Level: 'Below Manager',
      travelCost: '',
      stayCostDay: '',
      otherCostDay: '',
      remarks: '',
      transportCurrency: 'INR', // default currency to INR
      stayCurrency: 'INR',
      otherCurrency: 'INR',
      isEditing: true,
      rowHeight: 120,
    };
    // Add the new row at the beginning and reassign serial numbers
    const updatedRows = [newRow, ...rows].map((row, index) => ({
      ...row,
      srNo: index + 1,
    }));

    setRows(updatedRows);
  };

  const handleInputChange = (e, id, field) => {
    const value = e.target.value?.name || e.target.value;
    setRows((prevRows) =>
      prevRows.map((row) => {
        if (row.id === id) {
          const updatedRow = { ...row, [field]: value };
          if (field === 'City') {
            updateCountryForCity(updatedRow, value);
          }
          return updatedRow;
        }
        return row;
      })
    );
  };

  const updateCountryForCity = (row, cityName) => {
    const selectedCity = dropdownData.find((city) => city.city_name === cityName);
    row.Country = selectedCity ? selectedCity.country_name : '';
  };

  const saveRow = async (rowToSave, operationType) => {
    const payload = {
      city_id: dropdownData.find((city) => city.city_name === rowToSave.City)?.id,
      level: rowToSave.Level,
      remarks: rowToSave.remarks,
      transport_cost: rowToSave.travelCost,
      transport_cost_currency: currencies.find((currency) => currency.name === rowToSave.transportCurrency)?.id,
      stay_cost: rowToSave.stayCostDay,
      stay_cost_currency: currencies.find((currency) => currency.name === rowToSave.stayCurrency)?.id,
      other_cost: rowToSave.otherCostDay,
      other_cost_currency: currencies.find((currency) => currency.name === rowToSave.otherCurrency)?.id,
    };
    if (operationType === 'insert') {
      try {
        await UseAxios(saveTravelCost(payload));
        snackbarOpen('Data saved successfully', 'success');
      } catch (error) {
        console.error('Error saving row:', error.message || error);
        snackbarOpen('Oops! Please try again', 'warning');
        return;
      }
    } else if (operationType === 'update') {
      try {
        payload.id = rowToSave.id;
        await UseAxios(updateTravelCost(payload));
        snackbarOpen('Data updated successfully', 'success');
      } catch (error) {
        console.error('Error updating row:', error.message || error);
        snackbarOpen('Oops! Please try again', 'warning');
        return;
      }
    }
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.id === rowToSave.id
          ? { ...row, isEditing: false, isEdit: false }
          : row
      )
    );
    fetchData();
  };

  const handleSave = (id) => {
    const rowToSave = rows.find((row) => row.id === id);
    const operationType = rowToSave.isEdit ? 'update' : 'insert';
    saveRow(rowToSave, operationType);
  };

  const handleCancelRow = (rowId) => {
    setRows((prevRow) =>
      prevRow.some((row) => row.isEdit)
        ? prevRow.map((row) =>
          row.id === rowId
            ? { ...row.originalData, isEditing: false, isEdit: false }
            : row
        )
          .filter((row) => row.id) // filter all rows with `id`
          .map((row, index) => ({
            ...row,
            srNo: index + 1,
          }))
        : prevRow
          .filter((row) => row.id !== rowId)
          .map((row, index) => ({
            ...row,
            srNo: index + 1,
          }))
    );
  };

  const handleEdit = (id) => {
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.id === id
          ? { ...row, isEditing: true, isEdit: true, originalData: { ...row } }
          : row
      )
    );
  };

  const newRowTemplate = {
    City: '',
    Country: '',
    Level: '',
    travelCost: '',
    stayCostDay: '',
    otherCostDay: '',
  };

  const isRowUnchanged = (row) => {
    return Object.keys(newRowTemplate).some((key) => row[key] === newRowTemplate[key]);
  };

  const getSelectedValue = (name, options) => {
    return options.find(option => option.name === name) || null;
  };

  const columns = [
    {
      field: 'srNo',
      headerName: 'Sr.No.',
      flex: getColumnFlex(0.9),
      minWidth: 145,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'City',
      headerName: 'City',
      flex: getColumnFlex(1.8),
      minWidth: 150,
      sortable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) =>
        row.isEditing ? (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <SearchDropdown
              placeholder='Select'
              options={dropdownData}
              onChange={(e) => handleInputChange(e, row.id, 'City')}
              value={getSelectedValue(row.City, dropdownData)}
              dropdownCss="text-box-css"
            />
          </div>
        ) : (
          <label className="saved-value">{row.City}</label>
        ),
    },
    {
      field: 'Country',
      headerName: 'Country',
      flex: getColumnFlex(1.8),
      minWidth: 160,
      sortable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) =>
        row.isEditing ? (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <SearchDropdown
              placeholder='Select'
              value={row.Country}
              dropdownCss="text-box-css country"
              disabled={true}
            />
          </div>
        ) : (
          <label className="saved-value">{row.Country}</label>
        ),
    },
    {
      field: 'Level',
      headerName: 'Level',
      flex: getColumnFlex(2.4),
      minWidth: 160,
      sortable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) =>
        row.isEditing ? (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <SearchDropdown
              searchBox={false}
              options={levelOptions}
              onChange={(e) => handleInputChange(e, row.id, 'Level')}
              value={getSelectedValue(row.Level, levelOptions)}
              dropdownCss="text-box-css"
            />
          </div>
        ) : (
          <label className="saved-value">{row.Level}</label>
        ),
    },
    {
      field: 'travelCost',
      headerName: 'Transport Cost',
      flex: getColumnFlex(0.8),
      minWidth: 205,
      sortable: false,
      disableColumnMenu: true,
      headerClassName: 'travelCost-header',
      renderCell: ({ row }) =>
        row.isEditing ? (
          <div className="number-input-div">
            <SearchDropdown
              searchBox={false}
              options={currencies}
              onChange={(e) => handleInputChange(e, row.id, 'transportCurrency')}
              value={getSelectedValue(row.transportCurrency, currencies)}
              dropdownCss="currency-select"
            />
            <TextField
              defaultValue={row.travelCost}
              onChange={(e) => handleInputChange(e, row.id, 'travelCost')}
              onKeyDown={(e) => e.stopPropagation()}
              className="number-input"
              variant="outlined"
              size="small"
              inputProps={{
                inputMode: 'decimal',
                pattern: '[0-9]*[.]?[0-9]*',
              }}
              onInput={(e) => {
                e.target.value = e.target.value.replace(/[^0-9.]/g, '');
                const parts = e.target.value.split('.');
                if (parts.length > 2) {
                  e.target.value = parts[0] + '.' + parts.slice(1).join('');
                }
              }}
            />
          </div>
        ) : (
          <label className="saved-value travelCost-cell">
            {formatCurrency(row.travelCost, row.transportCurrency)}
          </label>
        ),
    },
    {
      field: 'stayCostDay',
      headerName: 'Stay Cost/Day',
      flex: getColumnFlex(0.75),
      minWidth: 200,
      sortable: false,
      disableColumnMenu: true,
      headerClassName: ' stayCost-header',
      renderCell: ({ row }) =>
        row.isEditing ? (
          <div className="number-input-div">
            <SearchDropdown
              searchBox={false}
              options={currencies}
              onChange={(e) => handleInputChange(e, row.id, 'stayCurrency')}
              value={getSelectedValue(row.stayCurrency, currencies)}
              dropdownCss="currency-select"
            />
            <TextField
              defaultValue={row.stayCostDay}
              onChange={(e) => handleInputChange(e, row.id, 'stayCostDay')}
              onKeyDown={(e) => e.stopPropagation()}
              className="number-input"
              variant="outlined"
              size="small"
              inputProps={{
                inputMode: 'decimal',
                pattern: '[0-9]*[.]?[0-9]*',
              }}
              onInput={(e) => {
                e.target.value = e.target.value.replace(/[^0-9.]/g, '');
                const parts = e.target.value.split('.');
                if (parts.length > 2) {
                  e.target.value = parts[0] + '.' + parts.slice(1).join('');
                }
              }}
            />
          </div>
        ) : (
          <label className="saved-value stayCost-cell">
            {formatCurrency(row.stayCostDay, row.stayCurrency)}
          </label>
        ),
    },
    {
      field: 'otherCostDay',
      headerName: 'Other Cost/Day',
      flex: getColumnFlex(0.9),
      minWidth: 205,
      sortable: false,
      disableColumnMenu: true,
      headerClassName: 'otherCost-header',
      renderCell: ({ row }) =>
        row.isEditing ? (
          <div className="number-input-div">
            <SearchDropdown
              searchBox={false}
              options={currencies}
              onChange={(e) => handleInputChange(e, row.id, 'otherCurrency')}
              value={getSelectedValue(row.otherCurrency, currencies)}
              dropdownCss="currency-select"
            />
            <TextField
              defaultValue={row.otherCostDay}
              onChange={(e) => handleInputChange(e, row.id, 'otherCostDay')}
              onKeyDown={(e) => e.stopPropagation()}
              className="number-input"
              variant="outlined"
              size="small"
              inputProps={{
                inputMode: 'decimal',
                pattern: '[0-9]*[.]?[0-9]*',
              }}
              onInput={(e) => {
                e.target.value = e.target.value.replace(/[^0-9.]/g, '');
                const parts = e.target.value.split('.');
                if (parts.length > 2) {
                  e.target.value = parts[0] + '.' + parts.slice(1).join('');
                }
              }}
            />
          </div>
        ) : (
          <label className="saved-value otherCost-cell">
            {formatCurrency(row.otherCostDay, row.otherCurrency)}
          </label>
        ),
    },
    {
      field: 'remarks',
      headerName: 'Remarks',
      flex: getColumnFlex(2.7),
      minWidth: 200,
      sortable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) =>
        row.isEditing ? (
          <TextField
            defaultValue={row.remarks}
            onChange={(e) => handleInputChange(e, row.id, "remarks")}
            onKeyDown={(e) => e.stopPropagation()}
            className="text-box-css remarks"
            variant="outlined"
            size="small"
          />
        ) : (
          <label className="saved-value">
            {row.remarks && row.remarks.trim() !== ''
              ? row.remarks
              : '-'}
          </label>
        ),
    },
    {
      width: getColumnWidth(60, 60, undefined),
      minWidth: 60,
      flex: getColumnFlex(0.1),
      headerClassName: 'action-column',
      cellClassName: 'action-cell',
      renderCell: ({ row }) => (
        <div className="mobile-action-icons">
          {!row?.isEditing ? (
            <button
              className="travel-action-edit-icon"
              aria-label="Edit"
              onClick={() => handleEdit(row.id)}
              disabled={!hasAccess('update')}
            >
              <img src={EditIcon} alt="Edit" />
            </button>
          ) : (
            <div className='save-and-cancel-button-main' style={{ paddingTop: isMobileView ? '0px' : '4px' }}>
              <button
                className='save-and-cancel-button save-button-tick-placing'
                style={{
                  backgroundColor: isRowUnchanged(row) ? '#a69df1' : '#6B5CE8',
                  color: '#ffff',
                }}
                onClick={() => handleSave(row.id)}
                disabled={isRowUnchanged(row)}
              >✔</button>
              <button
                className='save-and-cancel-button save-button-cross-placing'
                style={{
                  backgroundColor: 'transparent',
                  color: '#6B5CE8',
                  border: '1px solid #6B5CE8'
                }}
                onClick={() => handleCancelRow(row.id)}
              >X</button>
            </div>
          )}
        </div>
      ),
      sortable: false,
      disableColumnMenu: true,
    },
  ];

  const filteredRows = searchText
    ? rows.filter((row) =>
      Object.values(row).some((value) =>
        (value ? value.toString() : '').toLowerCase().includes(searchText)
      )
    )
    : rows;

  const sortFilterColumns = [
    columns[0].field,
    columns[1].field,
    columns[2].field,
    columns[3].field,
    columns[4].field,
    columns[5].field,
    columns[6].field,
    columns[7].field,
  ];

  const rowHeightCondition = (params) => {
    const row = rows.find((row) => row.id === params.id);
    return row?.isEditing ? 104 : 52;
  };

  return (
    <div className="travel-cost-outer-layer">
      <div className="travel-cost-map">
        <div className="travel-cost-search-and-buttons" style={{ marginBottom: '20px' }}>
          <div className="travel-cost-search">
            <img src={MagnifyingGlass} alt="Search" />
            <input
              id="travel-cost-search-input"
              type="text"
              placeholder="Search..."
              onChange={handleSearch}
            />
          </div>
        </div>
        <div className="travel-cost-container">
          <div className="travel-cost-header">
            <div className="travel-cost-title">Transport Cost</div>
            <div className="travel-cost-buttons">
              <div className="travel-cost-filter-and-add-button">
                <ButtonComponent
                  variant="contained"
                  label="Add"
                  onClick={handleAddRow}
                  startIcon={<AddIcon />}
                  disabled={!hasAccess('create')}
                />
              </div>
            </div>
          </div>
          <div className="travel-data-grid-container">
            <DataGridComponent
              dataRows={filteredRows}
              dataColumns={columns}
              isDealTransition={true}
              pageSize={10}
              isFilteredRows={filteredRows}
              sortFilterColumns={sortFilterColumns}
              isEdit={isEdit}
              gridCheckbox={false}
              onRowSelectionChange={() => { }}
              dataGridCss=""
              checkboxDisabled={false}
              rowHeightCondition={rowHeightCondition}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TravelCost;